import axios from "axios";

// Sprint-83.2 — runtime backend-URL detection.
// The deploy pipeline keeps overwriting `frontend/.env`'s
// `REACT_APP_BACKEND_URL` to the production hostname, which kills
// preview logins (cross-origin XHR + `withCredentials:true` blows up
// against `Access-Control-Allow-Origin: *`). Defence:
//   1. At boot, if `window.location.origin` differs from the build-time
//      `REACT_APP_BACKEND_URL`, prefer the page's own origin — the
//      backend MUST be reachable there because the page just loaded
//      from it through the same k8s ingress.
//   2. Persist the chosen URL in localStorage so subsequent loads skip
//      the probe.
//   3. Defensive probe of `/api/_runtime/config` confirms the backend
//      is reachable at that origin before we lock it in.
const BUILD_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function pickBaseUrl() {
  // SSR / build-time: there's no `window`. Just use whatever was baked in.
  if (typeof window === "undefined") return BUILD_URL;
  const origin = window.location.origin;
  // Honour any previously-discovered runtime override.
  try {
    const cached = localStorage.getItem("pickngo_runtime_api_url");
    if (cached) return cached.replace(/\/$/, "");
  } catch { /* localStorage blocked — ignore */ }
  // If the page origin doesn't match the build-time URL, prefer the
  // page origin. Same-origin XHR avoids the entire CORS class of bugs.
  if (BUILD_URL && origin && origin !== BUILD_URL) {
    try {
      console.warn(
        "[pickngo] Runtime backend URL override: build='%s' but page origin='%s'. Using page origin.",
        BUILD_URL, origin
      );
    } catch { /* noop */ }
    try { localStorage.setItem("pickngo_runtime_api_url", origin); } catch { /* noop */ }
    return origin;
  }
  return BUILD_URL || origin;
}

const RESOLVED_BASE = pickBaseUrl();

// Async, best-effort probe to confirm the override is reachable. If
// `/api/_runtime/config` is not 200 from the chosen origin, clear the
// cache so the next page load can re-evaluate.
if (typeof window !== "undefined" && RESOLVED_BASE) {
  // Don't await — boot must not block on this.
  fetch(`${RESOLVED_BASE}/api/_runtime/config`, { credentials: "omit" })
    .then((r) => {
      if (!r.ok) throw new Error(`probe ${r.status}`);
      // Tiny dev banner — only injected if origin != build URL.
      if (BUILD_URL && BUILD_URL !== window.location.origin) {
        const id = "pickngo-runtime-banner";
        if (!document.getElementById(id)) {
          const d = document.createElement("div");
          d.id = id;
          d.style.cssText = "position:fixed;bottom:8px;right:8px;z-index:99999;background:#fef3c7;color:#92400e;border:1px solid #fcd34d;border-radius:6px;padding:6px 10px;font:11px/1.3 system-ui;max-width:280px;pointer-events:none;opacity:.85";
          d.textContent = `Using runtime backend URL: ${RESOLVED_BASE}`;
          document.body && document.body.appendChild(d);
        }
      }
    })
    .catch(() => {
      try { localStorage.removeItem("pickngo_runtime_api_url"); } catch { /* noop */ }
    });
}

const api = axios.create({
  baseURL: `${RESOLVED_BASE}/api`,
  // Timeout so silent-hang requests (proxy stall, OSRM backend slow, cold
  // DB connection) surface as a clear axios `ECONNABORTED` error instead of
  // spinning forever. 45 s is generous enough for the slowest legitimate
  // call (full OSRM road-routing on a long lane) and short enough that
  // customers don't stare at a frozen Calculate button.
  timeout: 45000,
  headers: {
    "Content-Type": "application/json",
  },
  // Sprint 20 hybrid-auth: send HttpOnly `pickngo_auth` cookie alongside
  // the existing `Authorization: Bearer` header. The Bearer header is
  // still primary (localStorage-backed) — the cookie is a parallel auth
  // source so backend can read it as fallback. This unlocks XSS-resistant
  // auth without logging anyone out. localStorage removal is scheduled
  // for a later sprint once prod is confirmed stable.
  withCredentials: true,
});

// Add token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pickngo_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses - only redirect for protected routes
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || "";
    const isAuthEndpoint = url.includes("/auth/");

    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem("pickngo_token");
      localStorage.removeItem("pickngo_user");
      if (!["/login", "/", "/register"].includes(window.location.pathname)) {
        window.location.href = "/login";
      }
    }

    // Sprint-95 — Always attach a string `userMessage` to every axios error
    // so callers can `toast.error(err.userMessage)` without ever passing an
    // object/array to React. Pydantic 422 responses return an array of
    // {type,loc,msg,...} objects — without this, raw arrays bubble into
    // <Toast>{detail}</Toast> and React crashes with "Objects are not valid
    // as a React child".
    try {
      const data = error?.response?.data;
      if (Array.isArray(data?.detail)) {
        error.userMessage = data.detail
          .map((e) => {
            const field = (e?.loc || []).filter((p) => p !== "body").join(".");
            const m = typeof e?.msg === "string" ? e.msg : "Invalid value";
            return field ? `${field}: ${m}` : m;
          })
          .filter(Boolean)
          .join("; ") || "Validation failed";
      } else if (typeof data?.detail === "string") {
        error.userMessage = data.detail;
      } else if (data?.detail && typeof data.detail === "object") {
        error.userMessage =
          (typeof data.detail.message === "string" && data.detail.message) ||
          (typeof data.detail.msg === "string" && data.detail.msg) ||
          "Something went wrong";
      } else if (typeof data?.message === "string") {
        error.userMessage = data.message;
      } else if (typeof data?.code === "string") {
        error.userMessage = data.code;
      } else if (error.code === "ECONNABORTED") {
        error.userMessage = "Request timed out. Please try again.";
      } else if (typeof error?.message === "string" && error.message) {
        error.userMessage = error.message;
      } else {
        error.userMessage = "Something went wrong. Please try again.";
      }
    } catch {
      error.userMessage = "Something went wrong. Please try again.";
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail === "object") {
    // Strict address validation errors (Feb 2026 multi-city fix) carry
    // { code, field, message, address } — surface the friendly message.
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
  }
  return String(detail);
}

export default api;
